import {
  cloneDeepWith,
  isArray,
  isPlainObject,
  isString,
  omitBy,
  toString,
} from "lodash-es";
import {
  ACCOUNT_CREATION_BROWSER_TITLE,
  ADMIN_PORTAL_BROWSER_TITLE,
  CLIENT_PORTAL_BROWSER_TITLE,
  CONSUMER_USER,
  DESCRIPTION_EN,
  DESCRIPTION_SP,
  EMAIL,
  EMAIL_LANGUAGE,
  EMPTY,
  HOST_PORT,
  LOCALE,
  LOCALE_EN,
  PROVIDER_PORTAL_BROWSER_TITLE,
  PROVIDER_SITE_CERTIFIER,
  PROVIDER_STAFF,
  READ_ONLY_ADMIN,
  SCR_ADMIN,
  STATIC,
  SUPER_ADMIN,
  TYPE_STRING,
} from "../Constants/CommonConstants";
import {
  APPLICATION_STATUS_CONTINUE_INTAKE,
  APPLICATION_STATUS_CONTINUE_RAC,
  APPLICATION_STATUS_CONTINUE_REAPPLY,
  APPLICATION_STATUS_CONTINUE_RECERTIFY,
  APPLICATION_STATUS_RAC,
  APPLICATION_STATUS_REAPPLY,
  APPLICATION_STATUS_RECERTIFY,
  APPLICATION_STATUS_START_INTAKE,
  CONTINUE_APPLICATION,
  RAC_APPLICATION,
  REAPPLY_APPLICATION,
  RECERTIFY_APPLICATION,
} from "../Constants/MyProfileConstants";
import {
  API_STATUS_FAILURE,
  API_STATUS_LOADING,
  API_STATUS_SUCCESS,
  BUILDER_TYPE_FULFILLED,
  BUILDER_TYPE_PENDING,
  BUILDER_TYPE_REJECTED,
} from "../Constants/SliceAndThunkConstants";
import EndpointService from "./EndpointService";
import { PHONE } from "../Constants/LoginVerificationMethodsConstants";

export const getLocale = () => {
  const locale = localStorage.getItem(LOCALE);
  return locale || LOCALE_EN;
};

export function convertObjectValuesToStrings(obj) {
  if (!obj) {
    return null;
  }
  if (isArray(obj)) {
    return obj;
  }
  const cloneDeepMethod = (obj) => {
    return cloneDeepWith(obj, (value) => {
      if (isArray(value)) {
        return value;
      }
      if (!isPlainObject(value)) {
        const returnString = toString(value);
        if (returnString) {
          return returnString?.trim();
        }
        return undefined;
      }
      return undefined;
    });
  };

  const returnObj = cloneDeepMethod(obj);

  for (const [key, value] of Object.entries(returnObj)) {
    if (isArray(value)) {
      const updatedArr = value.map((item) => cloneDeepMethod(item));
      returnObj[key] = updatedArr;
    }
  }
  const omittedObj = omitBy(
    returnObj,
    (value) => !((isString(value) && value) || isArray(value))
  );
  return omittedObj;
}

export const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch (e) {
    return null;
  }
};

export const fetchResponseCodeAndDescription = (response) => {
  if (response?.payload?.data?.error) {
    return {
      code: response?.payload?.data?.message?.code,
      description: response?.payload?.data?.error?.message,
      data: response?.payload?.data,
    };
  }
  return {
    code: response?.payload?.data?.message?.code,
    description: response?.payload?.data?.message?.description,
    data: response?.payload?.data,
  };
};
export const fetchErrorResponseCodeAndDescription = (error) => {
  if (error?.response?.payload?.data?.error) {
    return {
      code: error?.response?.data?.message?.code,
      description: error?.response?.data?.error?.message,
      errorCode: error?.response?.data?.error?.errorCode,
      errorNumber: error?.response?.data?.error?.errorNumber,
    };
  }
  return {
    code: error?.response?.data?.message?.code,
    description: error?.response?.data?.message?.description,
    errorCode: error?.response?.data?.message?.code,
    errorNumber: error?.response?.data?.correlationId,
  };
};

export const apiSliceBuilderService = (builderType, action) => {
  //Guard Condition
  if (!builderType) {
    return null;
  }
  switch (builderType) {
    case BUILDER_TYPE_PENDING:
      return {
        status: API_STATUS_LOADING,
        message: null,
        error: null,
        correlationId: null,
        timestamp: null,
      };
    case BUILDER_TYPE_REJECTED:
      return {
        status: API_STATUS_FAILURE,
        message: action?.payload?.data?.message,
        error: action?.payload?.data?.error,
        correlationId: action?.payload?.data?.correlationId,
        timestamp: action?.payload?.data?.timestamp,
      };
    case BUILDER_TYPE_FULFILLED:
      return {
        status: API_STATUS_SUCCESS,
        message: action?.payload?.data?.message
          ? action?.payload?.data?.message
          : action?.payload?.message,
        error: action?.payload?.daa?.error,
        correlationId: action?.payload?.data?.correlationId,
        timestamp: action?.payload?.data?.timestamp,
      };
    default:
      return null;
  }
};

export const splitCorrelationId = (index, stringValue) => [
  stringValue?.slice(0, index),
  stringValue?.slice(index),
];

export const fetchUserRoleByValue = (userRoleReferenceData, value) => {
  if (!userRoleReferenceData) {
    return null;
  }
  return userRoleReferenceData?.find((role) => {
    return role.value === value;
  });
};

export const CONSUMER_USER_VALUE_TEXT = "Consumer";
export const PROVIDER_SITE_CERTIFIER_TEXT = "Provider_Site_Certifier";
export const PROVIDER_STAFF_TEXT = "Provider_Staff";
export const SUPER_ADMIN_TEXT = "Super_Admin";
export const SCR_ADMIN_TEXT = "SCR_Admin";
export const READ_ONLY_ADMIN_TEXT = "Read_Only_Admin";

/**
 *
 * @param {*} userRoleReferenceData Entire state data for userRole in referenceData slice.
 * @returns The object containing the consumer user role code.
 */
export const fetchConsumerUserRoleData = (userRoleReferenceData) => {
  return userRoleReferenceData.find((role) => {
    return CONSUMER_USER_VALUE_TEXT === role.valueText;
  });
};

export const getHasProfLicenseNoValue = (uiOptions, value) => {
  const uiOptionObject = uiOptions.find((obj) => obj.value === value);
  return uiOptionObject?.label;
};

export const navigateToFpactPage = (endpointName, path = "") => {
  const endpoint = EndpointService(endpointName);
  const currentLocation = window.location.href.split(STATIC);
  const externalLink = endpoint?.replace(HOST_PORT, currentLocation[0]);
  if (path) {
    window.location.href = externalLink + path;
  } else {
    window.location.href = externalLink;
  }
};

export const getFullName = (firstName, lastName) => {
  return `${firstName || ""} ${lastName || ""}`;
};

export const getInitials = (str) => {
  if (!str) {
    return EMPTY;
  }
  return str.charAt(0).toUpperCase();
};

export const getDocumentTitleByRole = (role, isProfileRoute) => {
  if (isProfileRoute) {
    switch (role) {
      case CONSUMER_USER:
        return CLIENT_PORTAL_BROWSER_TITLE;
      case SCR_ADMIN:
      case SUPER_ADMIN:
      case READ_ONLY_ADMIN:
        return ADMIN_PORTAL_BROWSER_TITLE;
      case PROVIDER_STAFF:
      case PROVIDER_SITE_CERTIFIER:
        return PROVIDER_PORTAL_BROWSER_TITLE;
      default:
        return ACCOUNT_CREATION_BROWSER_TITLE;
    }
  }
  return ACCOUNT_CREATION_BROWSER_TITLE;
};

export const trimData = (data) => {
  const dataCopy = { ...data };
  Object.keys(dataCopy).forEach(
    (k) =>
      (dataCopy[k] =
        typeof dataCopy[k] === TYPE_STRING ? dataCopy[k].trim() : dataCopy[k])
  );
  return dataCopy;
};

export const clearApplicationData = () => {
  sessionStorage.clear();
  const cookie = "FPACTJWTCookie=;samesite=strict;path=/;secure";
  document.cookie = cookie;
};

export const getDynamicText = (applicationStatus) => {
  switch (applicationStatus) {
    case APPLICATION_STATUS_START_INTAKE:
    case APPLICATION_STATUS_CONTINUE_INTAKE:
      return CONTINUE_APPLICATION;
    case APPLICATION_STATUS_REAPPLY:
    case APPLICATION_STATUS_CONTINUE_REAPPLY:
      return REAPPLY_APPLICATION;
    case APPLICATION_STATUS_RAC:
    case APPLICATION_STATUS_CONTINUE_RAC:
      return RAC_APPLICATION;
    case APPLICATION_STATUS_RECERTIFY:
    case APPLICATION_STATUS_CONTINUE_RECERTIFY:
      return RECERTIFY_APPLICATION;
    default:
      return CONTINUE_APPLICATION;
  }
};

export const displayActiveStatus = (appDisplayStatus) => {
  return [
    APPLICATION_STATUS_RAC,
    APPLICATION_STATUS_CONTINUE_RAC,
    APPLICATION_STATUS_CONTINUE_RECERTIFY,
    APPLICATION_STATUS_RECERTIFY,
  ].includes(appDisplayStatus);
};

export const fetchEmailLanguageOptions = (preferredLanguage) => {
  const emailLanguageOptions = preferredLanguage.filter((obj) => {
    return (
      obj.description === DESCRIPTION_EN || obj.description === DESCRIPTION_SP
    );
  });
  return emailLanguageOptions;
};

export const handlePayloadForEditOrRegisterVerification = (
  data,
  verificationMethod,
  flow,
  method,
  addEditEmail,
  isEmailLanguageChanged
) => {
  let payload = {};
  if (flow === PHONE) {
    payload = {
      otpType: flow,
      [verificationMethod]: data[flow],
    };
  }
  if (flow === EMAIL) {
    if (
      method === "add" ||
      addEditEmail !== data[flow] ||
      (addEditEmail === data[flow] && !isEmailLanguageChanged)
    ) {
      payload = {
        otpType: flow,
        prefEmailLanguage: data[EMAIL_LANGUAGE].description,
        [verificationMethod]: data[flow],
      };
    } else {
      payload = {
        prefEmailLanguage: data[EMAIL_LANGUAGE].description,
      };
    }
  }
  return payload;
};

export const transformProviderSiteList = (providerSiteList) => {
  const modifiedProviderSiteData = providerSiteList.map((siteData) => {
    return {
      businessName: siteData.businessName,
      serviceAddressLine1: siteData?.serviceAddressLine1,
      serviceAddressLine2: siteData?.serviceAddressLine2,
      city: siteData.city,
      state: siteData.state,
      zip: siteData.zip,
      npiId: siteData.npiId,
      value: siteData.providerSiteLocationId,
    };
  });
  return modifiedProviderSiteData;
};

export const getProviderLocationIdList = (siteInformationData) => {
  const providerIdList = siteInformationData.map(
    (obj) => obj.providerSiteLocationId
  );
  return providerIdList;
};

export const getEntitledLocationList = (associatedSites) => {
  return associatedSites.filter((site) => site.entitlementList.includes(1900));
};

export const getAssociatedLocationList = (associatedSites) => {
  return associatedSites.filter((site) => !site.entitlementList.includes(1900));
};
